import type { AnyDoc, ID, Project, TemplatePreset, ToolId } from '@/types';
import {
  isValid,
  migrateDoc,
  parseDoc,
  reconcileMissing,
  unionAssetIds,
  DOC_STORAGE_KEY,
  TEMPLATES_KEY,
  THEME_KEY,
  TOOL_IDS,
  TOOL_KEY,
  isToolId,
} from '@nextcollage/engine';
import { addBlob, blobFor, restoreAssets } from '@/media/assetStore';
import { pruneBlobs } from '@/media/db';

/**
 * Browser storage around the engine's document migrations. Everything that
 * decides what a saved document *means* lives in `@nextcollage/engine`; this
 * file only knows about localStorage, Blobs and files.
 */

export { unionAssetIds };

/** Pre-rename keys, read once so an existing session is not lost. */
const LEGACY_KEYS: Record<string, string> = {
  [DOC_STORAGE_KEY.collage]: 'collagen:project:v1',
  [TEMPLATES_KEY]: 'collagen:templates:v1',
  [THEME_KEY]: 'collagen:theme:v1',
};

function readSetting(key: string): string | null {
  try {
    const current = localStorage.getItem(key);
    if (current !== null) return current;
    const legacyKey = LEGACY_KEYS[key];
    if (!legacyKey) return null;
    const legacy = localStorage.getItem(legacyKey);
    if (legacy !== null) {
      localStorage.setItem(key, legacy);
      localStorage.removeItem(legacyKey);
    }
    return legacy;
  } catch {
    return null;
  }
}

function writeSetting(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* quota exceeded — the app still works, it just will not persist */
  }
}

/* ── Autosave ──────────────────────────────────────────────────────────── */

const saveTimers: Partial<Record<ToolId, number>> = {};
/** Sweeping every blob key on each 700 ms save would be wasteful. */
const PRUNE_INTERVAL = 30_000;
let lastPrune = 0;

export function scheduleSave(tool: ToolId, doc: AnyDoc, keep: ID[]) {
  window.clearTimeout(saveTimers[tool]);
  saveTimers[tool] = window.setTimeout(() => {
    writeSetting(DOC_STORAGE_KEY[tool], JSON.stringify(doc));
    const now = Date.now();
    if (now - lastPrune > PRUNE_INTERVAL) {
      lastPrune = now;
      void pruneBlobs(keep);
    }
  }, 700);
}

export function saveTool(tool: ToolId) {
  writeSetting(TOOL_KEY, tool);
}

export function loadTool(): ToolId | null {
  const t = readSetting(TOOL_KEY);
  return isToolId(t) ? t : null;
}

export interface Workspace {
  docs: Partial<Record<ToolId, AnyDoc>>;
  tool: ToolId | null;
  /** false when this browser has never used the app — drives the first-run chooser */
  hadSaved: boolean;
}

export async function loadWorkspace(): Promise<Workspace> {
  const docs: Partial<Record<ToolId, AnyDoc>> = {};
  let hadSaved = false;

  for (const tool of TOOL_IDS) {
    const raw = readSetting(DOC_STORAGE_KEY[tool]);
    if (raw === null) continue;
    hadSaved = true;
    const doc = parseDoc(tool, raw);
    if (doc) docs[tool] = doc;
  }

  const missing = await restoreAssets(unionAssetIds(Object.values(docs)));
  if (missing.length) {
    const gone = new Set(missing);
    for (const doc of Object.values(docs)) reconcileMissing(doc, gone);
  }

  return { docs, tool: loadTool(), hadSaved };
}

/* ── Portable .collage files (document + embedded photos) ──────────────── */

const BUNDLE_KIND = 'nextcollage';
/** Files written before the rename. */
const LEGACY_BUNDLE_KIND = 'collagen';

interface Bundle {
  kind: typeof BUNDLE_KIND | typeof LEGACY_BUNDLE_KIND;
  version: 1 | 2;
  tool?: ToolId;
  doc?: AnyDoc;
  /** v1 field, still written for collages so older builds can open the file */
  project?: Project;
  images: Record<string, string>;
}

function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

async function dataURLToBlob(url: string): Promise<Blob> {
  const res = await fetch(url);
  return res.blob();
}

export async function bundleDoc(tool: ToolId, doc: AnyDoc): Promise<Blob> {
  const images: Record<string, string> = {};
  for (const id of doc.assetOrder) {
    const blob = await blobFor(id).catch(() => undefined);
    if (blob) images[id] = await blobToDataURL(blob);
  }
  const bundle: Bundle = {
    kind: BUNDLE_KIND,
    version: 2,
    tool,
    doc,
    images,
    ...(tool === 'collage' ? { project: doc as Project } : {}),
  };
  return new Blob([JSON.stringify(bundle)], { type: 'application/json' });
}

export async function openBundle(file: File): Promise<{ tool: ToolId; doc: AnyDoc }> {
  const text = await file.text();
  const bundle = JSON.parse(text) as Bundle;
  const kind = bundle?.kind;
  if (kind !== BUNDLE_KIND && kind !== LEGACY_BUNDLE_KIND) {
    throw new Error('Not a NextCollage project file');
  }
  for (const [id, dataUrl] of Object.entries(bundle.images ?? {})) {
    try {
      await addBlob(id, await dataURLToBlob(dataUrl));
    } catch {
      /* skip unreadable image */
    }
  }

  const tool = bundle.version === 2 && bundle.tool ? bundle.tool : 'collage';
  const raw = (bundle.version === 2 ? bundle.doc : bundle.project) ?? bundle.project;
  if (!raw) throw new Error('Not a NextCollage project file');

  return { tool, doc: migrateDoc(tool, raw) };
}

/* ── User template library ─────────────────────────────────────────────── */

export function loadCustomTemplates(): TemplatePreset[] {
  try {
    const raw = readSetting(TEMPLATES_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as TemplatePreset[];
    return list.filter((t) => t?.template && isValid(t.template));
  } catch {
    return [];
  }
}

export function saveCustomTemplates(list: TemplatePreset[]) {
  writeSetting(TEMPLATES_KEY, JSON.stringify(list));
}

export function loadTheme(): 'light' | 'dark' | 'system' {
  try {
    const t = readSetting(THEME_KEY);
    return t === 'light' || t === 'dark' ? t : 'system';
  } catch {
    return 'system';
  }
}

export function saveTheme(theme: string) {
  writeSetting(THEME_KEY, theme);
}
